import { api } from './client';
import { getPatientById, getPatientByIdAllActivities } from './patients';
import type {
  Activity,
  ActivityListResponse,
  ActivityFilters,
  DownloadActivityRequest,
  ActivityResut,
  ActivityRawData,
} from '@/types/activity';
import type { DownloadStructure } from '@/types/download';
import { getCurrentSelfServiceBasePath } from './selfServiceBasePath';
import { applyActivityFilters } from '@/utils/activityFilters';
import { createResourceCache } from './cache/resourceCache';
import { transformApiActivity, type ApiActivity } from './transforms/activityTransform';

type PatientLookup = (patientId: string) => Promise<{
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  activities: unknown[];
} | null>;

async function getActivitiesForPatient(
  lookupPatient: PatientLookup,
  patientId: string,
  filters?: ActivityFilters,
  patientName?: string
): Promise<ActivityListResponse> {
  const patient = await lookupPatient(patientId);

  if (!patient) {
    return {
      activities: [],
      total: 0,
      page: 1,
      pageSize: 50,
    };
  }

  const rawActivities = patient.activities as ApiActivity[];
  const resolvedPatientName = patientName || `${patient.firstName} ${patient.lastName}`;
  let activities = rawActivities.map((raw, index) =>
    transformApiActivity(raw, patientId, resolvedPatientName, patient.username, index)
  );

  activities = applyActivityFilters(activities, filters);

  return {
    activities,
    total: activities.length,
    page: 1,
    pageSize: 50,
  };
}

/**
 * Get activities for a patient (Doctor view - uses patient list)
 * This should only be called by doctors viewing patient data
 */
export async function getActivities(
  patientId: string,
  filters?: ActivityFilters,
  patientName?: string
): Promise<ActivityListResponse> {
  return getActivitiesForPatient(getPatientById, patientId, filters, patientName);
}

export async function getActivitiesAll(
  patientId: string,
  filters?: ActivityFilters,
  patientName?: string
): Promise<ActivityListResponse> {
  return getActivitiesForPatient(getPatientByIdAllActivities, patientId, filters, patientName);
}

/**
 * Get activities for the current patient (Patient view - uses basic profile)
 * This should only be called by patients viewing their own data
 */
export async function getCurrentPatientActivities(
  filters?: ActivityFilters
): Promise<ActivityListResponse> {
  const profile = await getPatientBasicProfile();

  const rawActivities = profile.activities || [];
  const patientId = profile.username;
  const patientName = `${profile.name.first_name} ${profile.name.last_name}`;
  const patientUsername = profile.username;

  let activities = rawActivities.map((raw, index) =>
    transformApiActivity(raw, patientId, patientName, patientUsername, index)
  );

  activities = applyActivityFilters(activities, filters);

  return {
    activities,
    total: activities.length,
    page: 1,
    pageSize: 50,
  };
}

export async function getActivityById(
  activityId: string,
  patientId: string
): Promise<Activity | null> {
  const response = await getActivities(patientId);
  return response.activities.find(a => a.id === activityId) || null;
}

/**
 * Get a single activity for the current patient
 */
export async function getCurrentPatientActivityById(
  activityId: string
): Promise<Activity | null> {
  const response = await getCurrentPatientActivities();
  return response.activities.find(a => a.id === activityId) || null;
}

export async function downloadActivities(
  request: DownloadActivityRequest,
  structure: DownloadStructure = 'flat'
): Promise<Blob> {
  // Use POST /doctor/patients/activities/
  const jsonData = await api.post<ActivityResut[] | unknown>(
    '/doctor/patients/activities/',
    {
      data: request.data.map((item) => ({
        token: item.token,
        user_id: item.userId,
        activities: item.activities,
      })),
      include_macro: request.includeMacro ?? true,
      include_meta: request.includeMeta ?? true,
    }
  );

  // Import the ZIP utility dynamically to avoid circular dependencies
  const { createActivityZip } = await import('@/utils/zipDownload');

  // Create and return ZIP file
  return createActivityZip(jsonData, { structure });
}

export async function downloadActivitiesRaw(
  request: DownloadActivityRequest,
  structure: DownloadStructure = 'flat'
): Promise<Blob> {
  const jsonData = await api.post<import('@/types/activity').ActivityRawData[] | unknown>(
    '/doctor/patients/activities/raw/',
    {
      data: request.data.map((item) => ({
        token: item.token,
        user_id: item.userId,
        activities: item.activities,
      })),
    }
  );

  const { createActivityRawZip } = await import('@/utils/zipDownload');

  return createActivityRawZip(jsonData, { structure });
}


export async function getPatientActivities(
  patientId: string,
  filters?: ActivityFilters,
  patientName?: string
): Promise<ActivityListResponse> {
  return getActivities(patientId, filters, patientName);
}

// Patient-specific endpoints

type PatientBasicProfile = {
  username: string;
  name: { first_name: string; middle_name?: string; last_name: string };
  birthday?: string;
  activities: ApiActivity[];
};

const patientProfileCache = createResourceCache<PatientBasicProfile>();

/**
 * Clear the patient profile cache (called on logout)
 */
export function clearPatientProfileCache(): void {
  patientProfileCache.clear();
}

/**
 * Get basic profile for the current self-service user (patient / nushu-x)
 */
export async function getPatientBasicProfile(
  forceRefresh = false
): Promise<PatientBasicProfile> {
  if (!forceRefresh) {
    const cached = patientProfileCache.get();
    if (cached) {
      return cached;
    }

    const pending = patientProfileCache.getPending();
    if (pending) {
      return pending;
    }
  }

  const request = api
    .get<PatientBasicProfile>(`${getCurrentSelfServiceBasePath()}/profile/`)
    .then((data) => {
      patientProfileCache.set(data);
      return data;
    })
    .catch((error) => {
      patientProfileCache.setPending(null);
      throw error;
    });

  patientProfileCache.setPending(request);
  return request;
}

/**
 * Download activities for the current self-service user (patient / nushu-x)
 */
export async function downloadPatientActivities(
  activityIds: string[],
  options?: {
    includeMacro?: boolean;
    includeMeta?: boolean;
  },
  structure: DownloadStructure = 'flat'
): Promise<Blob> {
  const jsonData = await api.post<unknown>(
    `${getCurrentSelfServiceBasePath()}/activities/`,
    {
      activities: activityIds,
      include_macro: options?.includeMacro ?? true,
      include_meta: options?.includeMeta ?? true,
    }
  );

  if (!isActivityResultArray(jsonData)) {
    throw new Error('Unexpected response format for patient activity download');
  }

  // Get current patient profile for username
  const profile = await getPatientBasicProfile();

  // Import the ZIP utility dynamically
  const { createSinglePatientZip } = await import('@/utils/zipDownload');

  // Create and return ZIP file
  return createSinglePatientZip(jsonData, profile.username, { structure });
}

export async function downloadPatientActivitiesRaw(
  activityIds: string[],
  structure: DownloadStructure = 'flat'
): Promise<Blob> {
  const jsonData = await api.post<unknown>(
    `${getCurrentSelfServiceBasePath()}/activities/raw/`,
    {
      activities: activityIds,
    }
  );

  if (!isActivityRawDataArray(jsonData)) {
    throw new Error('Unexpected response format for patient raw activity download');
  }

  const profile = await getPatientBasicProfile();

  const { createSinglePatientRawZip } = await import('@/utils/zipDownload');

  return createSinglePatientRawZip(jsonData, profile.username, { structure });
}

function isActivityResult(value: unknown): value is ActivityResut {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.activity_id === 'string' &&
    'results' in record &&
    'meta' in record
  );
}

function isActivityResultArray(value: unknown): value is ActivityResut[] {
  return Array.isArray(value) && value.every(isActivityResult);
}

function isActivityRawData(value: unknown): value is ActivityRawData {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.activity_id === 'string' &&
    'raw_data' in record
  );
}

function isActivityRawDataArray(value: unknown): value is ActivityRawData[] {
  return Array.isArray(value) && value.every(isActivityRawData);
}
